import { Controller } from "@hotwired/stimulus"

// Job Manager (/djobs/:id/manage) — unified Photography + Confirms edit screen.
//
// Drives all the client behavior the Claude Design handoff specifies:
//  - tab switching (swaps the visible panel + updates the page header title,
//    subtitle, status pill and the footer Print button)
//  - the collapsible School Information card + its Hide/Show contacts toggle
//  - a live status pill derived from the Confirm Status field
//  - the panoramic-image file-name display
//  - keeping shared fields (rendered on both tabs) in sync as you type
//  - the Crew & Rigs repeater (Add Rig clones a template row; ✕ removes a row,
//    marking persisted rigs for destruction)
export default class extends Controller {
  static targets = [
    "photoPanel", "confirmsPanel", "photoNav", "confirmsNav",
    "title", "subtitle", "pill", "printBtn",
    "schoolBody", "schoolChevron", "schoolToggleLabel", "contactsBtn", "contactsGroup",
    "fileName", "rigsContainer", "rigTemplate", "sync", "weekday",
    "jobNoteValue", "pictureDayValue"
  ]
  static values = { startTab: String, confirmStatus: String }

  connect() {
    this.schoolOpen = true
    this.showContacts = true
    this.rigIndex = new Date().getTime()
    this.showTab(this.hasStartTabValue && this.startTabValue === "confirms" ? "confirms" : "photography")
  }

  // ---- Tabs ----
  showPhotography() { this.showTab("photography") }
  showConfirms() { this.showTab("confirms") }

  showTab(tab) {
    this.tab = tab
    const isPhoto = tab === "photography"
    this.photoPanelTarget.classList.toggle("is-active", isPhoto)
    this.confirmsPanelTarget.classList.toggle("is-active", !isPhoto)
    this.photoNavTarget.classList.toggle("is-active", isPhoto)
    this.confirmsNavTarget.classList.toggle("is-active", !isPhoto)

    this.titleTarget.textContent = isPhoto ? "Photography" : "Confirms"
    this.subtitleTarget.textContent = isPhoto
      ? "Picture-day production & on-site details"
      : "Scheduling & confirmation tracking"
    if (this.hasPrintBtnTarget) this.printBtnTarget.style.display = isPhoto ? "none" : ""
    this.refreshPill()
  }

  // ---- Status pill ----
  updateStatus(e) {
    this.confirmStatusValue = e.target.value
    this.refreshPill()
  }

  refreshPill() {
    const [label, klass] = this.tab === "confirms"
      ? this.confirmsPill(this.confirmStatusValue)
      : ["Scheduled", "is-blue"]
    this.pillTarget.textContent = label
    this.pillTarget.className = "jm-pill " + klass
  }

  confirmsPill(status) {
    switch (status) {
      case "confirmed": return ["Confirmed", "is-green"]
      case "declined": return ["Declined", "is-red"]
      case "tentative": return ["Tentative", "is-amber"]
      case "Welcome": return ["Welcome", "is-blue"]
      default: return ["Pending", "is-amber"]
    }
  }

  // ---- School card ----
  toggleSchool(e) {
    // The Delete Job link sits inside this row. Its click must bubble all the
    // way to document, where jquery_ujs's delegated handler turns data-method
    // into a DELETE + confirm — stopPropagation on the link would silence ujs
    // and the link would navigate as a plain GET. So the link lets the event
    // through and the toggle steps aside instead.
    if (e && e.target.closest(".jm-delete-btn")) return
    this.schoolOpen = !this.schoolOpen
    this.schoolBodyTarget.style.display = this.schoolOpen ? "" : "none"
    this.schoolChevronTarget.innerHTML = this.schoolOpen ? "&#9662;" : "&#9656;"
    this.schoolToggleLabelTarget.textContent = this.schoolOpen ? "Hide details" : "Show details"
    if (this.hasContactsBtnTarget) this.contactsBtnTarget.style.display = this.schoolOpen ? "" : "none"
  }

  toggleContacts(e) {
    if (e) e.stopPropagation()
    this.showContacts = !this.showContacts
    this.contactsGroupTarget.style.display = this.showContacts ? "contents" : "none"
    this.contactsBtnTarget.textContent = this.showContacts ? "Hide contacts" : "Show contacts"
  }

  // ---- Date weekday (shown next to the Date field) ----
  updateWeekday(e) {
    let label = ""
    const val = e.target.value
    if (val) {
      const d = new Date(val + "T00:00:00")
      if (!isNaN(d.getTime())) label = d.toLocaleDateString("en-US", { weekday: "long" })
    }
    this.weekdayTargets.forEach((el) => { el.value = label })
  }

  // ---- File picker ----
  updateFileName(e) {
    const file = e.target.files && e.target.files[0]
    this.fileNameTarget.textContent = file ? file.name : "No file chosen"
  }

  // ---- Shared-field sync (mirror same-keyed inputs across tabs) ----
  syncField(e) {
    const key = e.target.dataset.syncKey
    if (!key) return
    this.syncTargets.forEach((el) => {
      if (el !== e.target && el.dataset.syncKey === key && el.value !== e.target.value) {
        el.value = e.target.value
      }
    })
  }

  // ---- Structured note template (gray labels + black values) ----
  // Serialize the edited template editor's "Label value" lines into the single
  // hidden field for that note column (job_note / job_note_picture_day).
  rebuildNote(e) {
    const editor = e.target.closest(".jm-note-template")
    if (!editor) return
    const lines = []
    editor.querySelectorAll(".jm-note-row").forEach((row) => {
      const label = row.querySelector(".jm-note-label").textContent.trim()
      const value = row.querySelector(".jm-note-value").value
      lines.push(value ? `${label} ${value}` : label)
    })
    const combined = lines.join("\n")
    const target = e.target.dataset.noteTarget
    if (target === "jobNoteValue" && this.hasJobNoteValueTarget) this.jobNoteValueTarget.value = combined
    if (target === "pictureDayValue" && this.hasPictureDayValueTarget) this.pictureDayValueTarget.value = combined
  }

  // ---- Crew & rigs repeater ----
  addRig() {
    const html = this.rigTemplateTarget.innerHTML.replace(/NEW_RECORD/g, this.rigIndex++)
    this.rigsContainerTarget.insertAdjacentHTML("beforeend", html)
  }

  removeRig(e) {
    const row = e.target.closest('[data-job-manager-target="rigRow"]')
    if (!row) return
    const idField = row.querySelector('input[name*="[id]"]')
    if (idField && idField.value) {
      const destroy = row.querySelector('input[name*="[_destroy]"]')
      if (destroy) destroy.value = "1"
      row.style.display = "none"
    } else {
      row.remove()
    }
  }
}
